import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '../useApiClient'
import {
  listKnownUsers,
  listCollectionShares,
  shareCollection,
  unshareCollection,
} from '../endpoints/share'
import type { CollectionShare } from '../endpoints/share'

export function useKnownUsers() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['v_known_users'] as const,
    queryFn: () => listKnownUsers(client),
  })
}

export function useCollectionShares(collectionId: string | null) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['v_collection_shares', collectionId] as const,
    queryFn: () => listCollectionShares(client, collectionId!),
    enabled: collectionId !== null,
  })
}

export function useShareCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      collectionId,
      userAuthId,
    }: {
      collectionId: string
      userAuthId: string
      userEmail: string
    }) => shareCollection(client, collectionId, userAuthId),
    onMutate: async ({ collectionId, userAuthId, userEmail }) => {
      const key = ['v_collection_shares', collectionId] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<CollectionShare[]>(key)
      qc.setQueryData<CollectionShare[]>(key, (old) => [
        ...(old ?? []),
        { collection_id: collectionId, user_id: userAuthId, user_email: userEmail },
      ])
      return { previous, key }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSettled: (_data, _err, { collectionId }) => {
      qc.invalidateQueries({ queryKey: ['v_collection_shares', collectionId] })
      qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] })
    },
  })
}

export function useUnshareCollection() {
  const client = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, userAuthId }: { collectionId: string; userAuthId: string }) =>
      unshareCollection(client, collectionId, userAuthId),
    onMutate: async ({ collectionId, userAuthId }) => {
      const key = ['v_collection_shares', collectionId] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<CollectionShare[]>(key)
      qc.setQueryData<CollectionShare[]>(key, (old) =>
        (old ?? []).filter((s) => s.user_id !== userAuthId),
      )
      return { previous, key }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSettled: (_data, _err, { collectionId }) => {
      qc.invalidateQueries({ queryKey: ['v_collection_shares', collectionId] })
      qc.invalidateQueries({ queryKey: ['case_collection', 'owned'] })
    },
  })
}
